import { DropboxFileItem } from '@common/types/dropbox';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { APP_PREFIX_PATH } from '@constants/common';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { Grid2, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getError, handleError } from '@utils/common';
import { triggerBrowserDownload } from '@utils/file';
import { useCallback, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { MediaItemCard } from '../../../features/mediaAssets/components/display/MediaItemCard';

type ToggleFavoriteArgs = {
    path: string;
    folderPath: string;
};

type LoadDropboxGridPageArgs = {
    queryPath: string;
    pageSize: number;
    loadedPaths: string[];
};

type DropboxFilesGridViewProps = {
    routeSegment: string;
    downloadType: string;
    loadErrorMessage: MessageDescriptor;
    loadFailedMessage: MessageDescriptor;
    emptyListMessage: MessageDescriptor;
    loadPage: (args: LoadDropboxGridPageArgs) => Promise<{ results: DropboxFileItem[]; count?: number }>;
    onFavorite?: (args: ToggleFavoriteArgs) => Promise<unknown>;
    onUnfavorite?: (args: ToggleFavoriteArgs) => Promise<unknown>;
    onVisibleMediaItemsChange?: (items: DropboxFileItem[]) => void;
    onMediaItemClick?: (item: DropboxFileItem) => void;
    externalFavoriteStates?: Record<string, boolean>;
    onFavoriteStateChange?: (args: { path: string; isFavorite: boolean }) => void;
};

export const DropboxFilesGridView = ({
    routeSegment,
    downloadType,
    loadErrorMessage,
    loadFailedMessage,
    emptyListMessage,
    loadPage,
    onFavorite,
    onUnfavorite,
    onVisibleMediaItemsChange,
    onMediaItemClick,
    externalFavoriteStates,
    onFavoriteStateChange,
}: DropboxFilesGridViewProps) => {
    const { pathname } = useLocation();
    const intl = useIntl();
    const scrollElement = useMaxWidthWrapperScrollElement();

    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const pageSize = isLg ? 12 : isMd ? 9 : isSm ? 6 : 3;
    const queryPath = pathname.split('/').filter(Boolean).slice(2).map(decodeURIComponent).join('/');

    const [loadError, setLoadError] = useState<unknown>(null);

    const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
    const [favoritingFiles, setFavoritingFiles] = useState<Set<string>>(new Set());
    const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({});

    const getDisplayIsFavorite = useCallback(
        (item: DropboxFileItem) => {
            const itemPath = item.path ?? '';
            if (itemPath in (externalFavoriteStates ?? {})) {
                return Boolean(externalFavoriteStates?.[itemPath]);
            }

            return itemPath in favoriteOverrides ? favoriteOverrides[itemPath] : Boolean(item.is_favorite);
        },
        [externalFavoriteStates, favoriteOverrides]
    );

    const loadGridPage = useCallback(
        async ({ pageSize: nextPageSize, loadedPaths }: { page: number; pageSize: number; loadedPaths?: string[] }) => {
            setLoadError(null);

            return loadPage({
                queryPath,
                pageSize: nextPageSize,
                loadedPaths: loadedPaths ?? [],
            });
        },
        [loadPage, queryPath]
    );

    const { items, sentinelRef, hasRealItems, isLoadingMore } = useInfiniteLazyLoader<DropboxFileItem>({
        pageSize,
        scrollElement,
        contextKey: `${routeSegment}:${queryPath}:${pageSize}`,
        loadPage: loadGridPage,
        getItemKey: (item) => item.path ?? item.name ?? '',
        getLoadedPaths: (loadedItems) => loadedItems.map((item) => item.path ?? '').filter(Boolean),
        countStrategy: 'remaining',
        onError: (error) => {
            setLoadError(error);
            console.error(intl.formatMessage(loadErrorMessage), error);
            handleError(intl.formatMessage(loadErrorMessage), error);
        },
    });

    const handleDownload = async (item: DropboxFileItem) => {
        const itemPath = item.path ?? '';
        if (!itemPath || downloadingFiles.has(itemPath)) {
            return;
        }

        setDownloadingFiles((prev) => new Set(prev).add(itemPath));
        try {
            await triggerBrowserDownload(
                `/api-professional/dropbox/download/?path=${encodeURIComponent(itemPath)}&type=${downloadType}`
            );
        } finally {
            setDownloadingFiles((prev) => {
                const next = new Set(prev);
                next.delete(itemPath);
                return next;
            });
        }
    };

    const handleToggleFavorite = useCallback(
        async (item: DropboxFileItem, isFavorite: boolean) => {
            const itemPath = item.path ?? '';
            const nextFavorite = !isFavorite;
            if (!itemPath || favoritingFiles.has(itemPath) || !onFavorite || !onUnfavorite) {
                return;
            }

            setFavoritingFiles((prev) => new Set(prev).add(itemPath));
            setFavoriteOverrides((prev) => ({ ...prev, [itemPath]: nextFavorite }));
            onFavoriteStateChange?.({ path: itemPath, isFavorite: nextFavorite });

            try {
                if (isFavorite) {
                    await onUnfavorite({ path: itemPath, folderPath: queryPath });
                } else {
                    await onFavorite({ path: itemPath, folderPath: queryPath });
                }
            } catch (error) {
                setFavoriteOverrides((prev) => ({ ...prev, [itemPath]: isFavorite }));
                onFavoriteStateChange?.({ path: itemPath, isFavorite });
                handleError(
                    intl.formatMessage({
                        id: 'filelist.favoriteError',
                        defaultMessage: 'Error updating favorite status',
                    }),
                    error
                );
            } finally {
                setFavoritingFiles((prev) => {
                    const next = new Set(prev);
                    next.delete(itemPath);
                    return next;
                });
            }
        },
        [favoritingFiles, intl, onFavorite, onUnfavorite, queryPath, onFavoriteStateChange]
    );

    useEffect(() => {
        setLoadError(null);
        setFavoriteOverrides({});
    }, [pageSize, queryPath, routeSegment]);

    useEffect(() => {
        if (!onVisibleMediaItemsChange) {
            return;
        }

        const visibleItems = items.filter(
            (item): item is DropboxFileItem =>
                !isInfiniteLoadingItem(item) && ['image', 'video'].includes(item.file_type ?? '')
        );
        onVisibleMediaItemsChange(visibleItems);
    }, [items, onVisibleMediaItemsChange]);

    return (
        <>
            <Grid2 container spacing={2} columns={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
                {hasRealItems || isLoadingMore ? (
                    items.map((item) => {
                        if (isInfiniteLoadingItem(item)) {
                            return (
                                <Grid2 key={item.name} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                                    <MediaItemCard loading />
                                </Grid2>
                            );
                        }

                        const isFavorite = getDisplayIsFavorite(item);

                        return (
                            <Grid2 key={item.path ?? 'unknown'} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                                <MediaItemCard
                                    {...item}
                                    is_favorite={isFavorite}
                                    baseUrl={`${APP_PREFIX_PATH}/${routeSegment}${
                                        item.path?.startsWith('/') ? '' : '/'
                                    }`}
                                    onClick={
                                        !!onMediaItemClick && ['image', 'video'].includes(item.file_type ?? '')
                                            ? () => {
                                                  onMediaItemClick({
                                                      ...item,
                                                      is_favorite: isFavorite,
                                                  });
                                              }
                                            : undefined
                                    }
                                    downloadProps={{
                                        onClick: () => {
                                            handleDownload(item);
                                        },
                                        loading: downloadingFiles.has(item.path ?? ''),
                                    }}
                                    favoriteProps={
                                        onFavorite && onUnfavorite
                                            ? {
                                                  onClick: () => {
                                                      handleToggleFavorite(item, isFavorite);
                                                  },
                                                  loading: favoritingFiles.has(item.path ?? ''),
                                              }
                                            : undefined
                                    }
                                />
                            </Grid2>
                        );
                    })
                ) : loadError ? (
                    <div>
                        <Typography variant="body1">{intl.formatMessage(loadFailedMessage)}</Typography>
                        {getError(loadError) ?? ''}
                    </div>
                ) : (
                    <Typography variant="body1">{intl.formatMessage(emptyListMessage)}</Typography>
                )}
            </Grid2>
            <div ref={sentinelRef} className="h-px w-full" aria-hidden />
        </>
    );
};
